import { ComponentProps, useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';
import { Modal as RSModal } from 'reactstrap';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button, SimpleTable, Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { ListingDetails, SplitListingInput } from 'src/WebApiController';

import { getSplitListingToOriginalInput } from './SplitListingToOriginal.util';

export type SplitListingToOriginalDialogProps = ComponentProps<
  typeof RSModal
> & {
  onOkay: (splitListingInput: SplitListingInput) => Promise<void>;
  onCancel: () => void;
};

export function SplitListingToOriginalDialog({
  onOkay,
  onCancel,
  ...rest
}: SplitListingToOriginalDialogProps) {
  const { posEntity: listing, isLoading: isDataLoading } =
    useActivePosEntityContext<ListingDetails>();
  const { activeAccountWebClientConfig } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const { showErrorDialog } = useClientLogContext();

  const [splitListingInput, setSplitListingInput] =
    useState<SplitListingInput | null>();

  useEffect(() => {
    if (listing?.tickets) {
      // Only do this if there are tickets
      const newInput = getSplitListingToOriginalInput(listing);
      if (newInput && !isEqual(newInput, splitListingInput)) {
        setSplitListingInput(newInput);
      }
    }
  }, [
    activeAccountWebClientConfig,
    listing,
    showErrorDialog,
    splitListingInput,
  ]);

  return (
    <GenericDialog
      {...rest}
      size="m"
      header={<Content id={ContentId.ResetToOriginal} />}
      onOpened={() => {
        setSplitListingInput(undefined);
      }}
      onClick={(e) => {
        // This so that this dialog does not cause event to flow to the EventHeader open/close event
        e.stopPropagation();
        e.preventDefault();
      }}
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => {
              onCancel();
            }}
            disabled={isLoading || isDataLoading}
          >
            <Content id={ContentId.Cancel} />
          </Button>

          <Button
            disabled={isLoading || isDataLoading || !splitListingInput}
            onClick={async () => {
              setIsLoading(true);
              await onOkay(splitListingInput!);
              setIsLoading(false);
            }}
          >
            <Content id={ContentId.Confirm} />
          </Button>
        </>
      }
      onCancel={isLoading || isDataLoading ? undefined : onCancel}
    >
      <Stack direction="column" gap="m">
        {isLoading || isDataLoading || !splitListingInput ? (
          <PosSpinner />
        ) : (
          <>
            <Content id={ContentId.ListingWillBeSplitInto} />
            <SimpleTable.Table>
              <SimpleTable.Thead>
                <SimpleTable.Tr>
                  <SimpleTable.Th style={{ width: '33%' }}>
                    <Content id={ContentId.Row} />
                  </SimpleTable.Th>
                  <SimpleTable.Th style={{ width: '33%' }}>
                    <Content id={ContentId.Seat} />
                  </SimpleTable.Th>
                  <SimpleTable.Th style={{ width: '33%' }}>
                    <Content id={ContentId.Listing} />
                  </SimpleTable.Th>
                </SimpleTable.Tr>
              </SimpleTable.Thead>
              <SimpleTable.Tbody>
                {(splitListingInput?.ticketsByListingNumber ?? []).map(
                  (ticketListingNumber) => {
                    return (
                      <SimpleTable.Tr key={ticketListingNumber.listingNumber}>
                        <SimpleTable.Td>
                          {ticketListingNumber.ticket.row}
                        </SimpleTable.Td>
                        <SimpleTable.Td>
                          {ticketListingNumber.ticket.seat}
                        </SimpleTable.Td>
                        <SimpleTable.Td>
                          {ticketListingNumber.listingNumber}
                        </SimpleTable.Td>
                      </SimpleTable.Tr>
                    );
                  }
                )}
              </SimpleTable.Tbody>
            </SimpleTable.Table>
          </>
        )}
      </Stack>
    </GenericDialog>
  );
}
